// src/sectors/pilates/staff/permissions/permsSchema.js

/**
 * ✅ Tek gerçek kaynak: label + description + icon + quick + requires
 * - UI (modal/table/detail) bu şemadan çizilir.
 * - defaultPerms / normalizePerms / applyRequires aynen çalışır.
 *
 * Terminoloji:
 * - "Own" => sadece kendine atanmış (örn. kendi seansları / kendi üyeleri)
 * - "All" => işletmenin tamamı
 *
 * requires:
 * - Row-level requires: bu satırdaki scope’lardan herhangi biri ON olunca listedeki required permission’lar da ON olur.
 * - Ayrıca required key OFF olunca ona bağlı (dependent) yetkiler otomatik OFF edilir.
 */

export const PERMS_SCHEMA = [
  // -------------------- Genel / Dashboard --------------------
  {
    baseKey: 'dashboard',
    label: 'Dashboard',
    group: 'Genel',
    icon: 'dashboard',
    description: 'Genel panel ekranlarını ve özet metrikleri görme yetkileri.',
    quick: true,
    scopes: [
      {
        key: 'canViewDashboardOwn',
        label: 'Kendi Paneli',
        description: 'Sadece kendine ait dashboard verilerini görür.',
        icon: 'person',
        default: true,
        quick: true,
      },
      {
        key: 'canViewDashboardCompany',
        label: 'İşletme Dashboard',
        description: 'İşletmenin genel dashboard verilerini görür.',
        icon: 'business',
        default: false,
        quick: true,
      },
    ],
  },

  // -------------------- Üye / Görüntüleme --------------------
  {
    baseKey: 'membersView',
    label: 'Üyeleri Gör',
    group: 'Üye',
    icon: 'groups',
    description: 'Üye listesi / profil ekranlarını görüntüleme yetkileri.',
    quick: true,
    scopes: [
      {
        key: 'canViewMembersOwn',
        label: 'Kendi Üyeleri',
        description: 'Sadece kendine atanmış üyeleri görür.',
        icon: 'group',
        default: true,
        quick: true,
      },
      {
        key: 'canViewMembersAll',
        label: 'Tüm Üyeler',
        description: 'Tüm işletme üyelerini görür.',
        icon: 'groups',
        default: false,
        quick: true,
      },
    ],
  },

  // -------------------- Üye / Düzenleme --------------------
  {
    baseKey: 'membersEdit',
    label: 'Üye Düzenle',
    group: 'Üye',
    icon: 'manage_accounts',
    description: 'Üye bilgilerini güncelleme / düzenleme yetkileri.',
    quick: false,
    scopes: [
      {
        key: 'canEditMembersOwn',
        label: 'Kendi Üyeleri',
        description: 'Kendine atanmış üyelerin bilgilerini düzenleyebilir.',
        icon: 'edit',
        default: false,
        requires: ['canViewMembersOwn'], // ✅ SADECE own view
      },
      {
        key: 'canEditMembersAll',
        label: 'Tüm Üyeler',
        description: 'Tüm işletme üyelerinin bilgilerini düzenleyebilir.',
        icon: 'edit_note',
        default: false,
        requires: ['canViewMembersAll'], // ✅ SADECE all view
      },
    ],
  },

  // -------------------- Üye / Oluşturma --------------------
  {
    baseKey: 'membersCreate',
    label: 'Üye Oluştur',
    group: 'Üye',
    icon: 'person_add',
    description: 'Yeni üye kaydı açma ve ilk borç/tahsilat işlemlerini başlatma yetkileri.',
    // ✅ create için üyeleri görme şart (en az own/all)

    quick: true,
    scopes: [
      {
        key: 'canCreateMember',
        label: 'Üye Oluştur',
        description: 'Yeni üye kaydı oluşturabilir.',
        icon: 'person_add',
        default: false,
        quick: true,
        requires: ['canViewMembersOwn'], // ✅ çoğu işletmede yeterli
      },
      {
        key: 'canCreateMemberPackage',
        label: 'Paket Oluştur',
        description: 'Mevcut üyeye sonradan paket/plan oluşturabilir.',
        icon: 'playlist_add',
        default: false,
        quick: false,
        requires: ['canViewMembersOwn'], // veya ALL isteyen varsa ['canViewMembersAll']
      },
    ],
  },

  // -------------------- Seans / Görüntüleme --------------------
  {
    baseKey: 'sessionsView',
    label: 'Seansları Gör',
    group: 'Seans',
    icon: 'event',
    description: 'Takvim / seans liste ekranlarını görüntüleme yetkileri.',
    quick: true,
    scopes: [
      {
        key: 'canViewSessionsOwn',
        label: 'Kendi Seansları',
        description: 'Sadece kendine ait / kendine atanmış seansları görür.',
        icon: 'event_available',
        default: true,
        quick: true,
      },
      {
        key: 'canViewSessionsAll',
        label: 'Tüm Seanslar',
        description: 'Takvimdeki tüm seansları görür.',
        icon: 'calendar_month',
        default: false,
        quick: true,
      },
    ],
  },

  // -------------------- Seans / Düzenleme --------------------
  {
    baseKey: 'sessionsEdit',
    label: 'Seans Düzenle',
    group: 'Seans',
    icon: 'edit_calendar',
    description: 'Seans oluşturma / düzenleme / iptal gibi işlemleri yapma yetkileri.',
    quick: false,
    scopes: [
      {
        key: 'canEditSessionsOwn',
        label: 'Kendi Seansları',
        description: 'Kendine ait seanslarda değişiklik yapabilir.',
        icon: 'edit_calendar',
        default: false,
        // ✅ sadece kendi seanslarını görebilmeli
        requires: ['canViewSessionsOwn'],
      },
      {
        key: 'canEditSessionsAll',
        label: 'Tüm Seanslar',
        description: 'Tüm seanslarda değişiklik yapabilir.',
        icon: 'event_repeat',
        default: false,
        // ✅ tüm seansları görebilmeli (istersen own da ekleyebilirsin)
        requires: ['canViewSessionsAll'],
      },
    ],
  },

  // -------------------- Finans --------------------
  {
    baseKey: 'finance',
    label: 'Finans İşlemleri',
    group: 'Finans',
    icon: 'payments',
    description: 'Ödeme alma ve gider/masraf işlemleri yetkileri.',
    quick: true,
    scopes: [
      {
        key: 'canAddExpense',
        label: 'Gider Ekle',
        description: 'Gider/masraf kaydı oluşturabilir.',
        icon: 'request_quote',
        default: false,
        quick: true,
      },
      {
        key: 'canTakePayment',
        label: 'Ödeme Al',
        description: 'Üyelerden ödeme kaydı oluşturabilir.',
        icon: 'payments',
        default: false,
        quick: true,
      },
    ],
  },

  // -------------------- Personel --------------------
  {
    baseKey: 'staff',
    label: 'Çalışanlar',
    group: 'Personel',
    icon: 'badge',
    description: 'Personel listeleme ve düzenleme yetkileri.',
    quick: true,
    scopes: [
      {
        key: 'canViewStaff',
        label: 'Çalışanları Gör',
        description: 'Çalışan listesini ve detaylarını görüntüleyebilir.',
        icon: 'badge',
        default: false,
        quick: true,
      },
      {
        key: 'canEditStaff',
        label: 'Çalışan Düzenle',
        description: 'Çalışan bilgilerini ve yetkilerini düzenleyebilir.',
        icon: 'admin_panel_settings',
        default: false,
        quick: true,
        requires: ['canViewStaff'],
      },
    ],
  },
  // -------------------- Yoklama / Attendance --------------------
  {
    baseKey: 'attendance',
    label: 'Yoklama',
    group: 'Seans',
    icon: 'how_to_reg',
    description: 'Seans katılım (yoklama) durumlarını işaretleme yetkileri.',
    quick: true,
    scopes: [
      {
        key: 'canMarkAttendanceOwn',
        label: 'Kendi Seansları',
        description: 'Sadece kendine ait seanslarda yoklama işaretleyebilir.',
        icon: 'person',
        default: false,
        quick: true,
        requires: ['canViewSessionsOwn'], // ✅ sadece OWN view
      },
      {
        key: 'canMarkAttendanceAll',
        label: 'Tüm Seanslar',
        description: 'İşletmedeki tüm seanslarda yoklama işaretleyebilir.',
        icon: 'groups',
        default: false,
        quick: true,
        requires: ['canViewSessionsAll'], // ✅ sadece ALL view
      },
    ],
  },
];

/* ---------------------------------------------
 * Helpers
 * ------------------------------------------- */

export function groupPerms(schema = PERMS_SCHEMA) {
  const g = {};
  schema.forEach((row) => {
    const k = row.group || 'Diğer';
    if (!g[k]) g[k] = [];
    g[k].push(row);
  });
  return g;
}

export function flattenScopes(schema = PERMS_SCHEMA) {
  const out = [];
  schema.forEach((row) => {
    (row.scopes || []).forEach((s) => {
      out.push({
        ...s,
        group: row.group,
        baseKey: row.baseKey,
        rowLabel: row.label,
        rowIcon: row.icon,
        rowDescription: row.description,
      });
    });
  });
  return out;
}

export function defaultPerms(schema = PERMS_SCHEMA) {
  const out = {};
  schema.forEach((row) => {
    (row.scopes || []).forEach((s) => {
      out[s.key] = !!s.default;
    });
  });
  return out;
}

export function normalizePerms(p, schema = PERMS_SCHEMA) {
  const base = defaultPerms(schema);
  if (!p) return base;
  const out = { ...base };
  Object.keys(base).forEach((k) => {
    out[k] = !!p[k];
  });
  return out;
}

/**
 * childKey -> [requiredKey...]
 * - Row-level requires: aynı row içindeki TÜM scope’lara uygulanır.
 *   (Zaten “edit” row’larında kullanıyorsun, bu yüzden en temiz/öngörülebilir yaklaşım.)
 */
export function buildRequiresIndex(schema = PERMS_SCHEMA) {
  const requiresByKey = {};

  schema.forEach((row) => {
    const rowReq = Array.isArray(row.requires) ? row.requires : [];

    (row.scopes || []).forEach((s) => {
      const scopeReq = Array.isArray(s.requires) ? s.requires : null;
      const req = scopeReq && scopeReq.length ? scopeReq : rowReq;

      if (req.length) requiresByKey[s.key] = req.slice();
    });
  });

  return requiresByKey;
}

/**
 * applyRequires
 * - toggle ON: required'ları ON yapar
 * - toggle OFF:
 *   - toggled OFF
 *   - bu permission bir "required" ise, ona bağlı (dependent) permission'ları da OFF yapar
 */
export function applyRequires(perms, toggledKey, nextValue, schema = PERMS_SCHEMA) {
  const out = { ...(perms || {}) };
  const requiresByKey = buildRequiresIndex(schema);

  // --- toggle ON
  if (nextValue) {
    const reqs = requiresByKey[toggledKey] || [];
    reqs.forEach((rk) => {
      out[rk] = true;
    });
    out[toggledKey] = true;
    return out;
  }

  // --- toggle OFF
  out[toggledKey] = false;

  // reverse index: requiredKey -> [childKeys...]
  const dependentsByReq = {};
  Object.keys(requiresByKey).forEach((child) => {
    (requiresByKey[child] || []).forEach((req) => {
      if (!dependentsByReq[req]) dependentsByReq[req] = [];
      dependentsByReq[req].push(child);
    });
  });

  const dependents = dependentsByReq[toggledKey] || [];
  dependents.forEach((ck) => {
    out[ck] = false;
  });

  return out;
}

/**
 * Table gibi yerlerde "hızlı ikonlar" için:
 * quick:true olan scope’ları döndürür.
 */
export function buildQuickPermIcons(schema = PERMS_SCHEMA, max = 6) {
  const scopes = flattenScopes(schema)
    .filter((s) => s.quick && s.icon)
    .map((s) => ({ key: s.key, label: s.label, icon: s.icon }));

  // uniq + limit
  const uniq = [];
  const seen = new Set();
  for (const it of scopes) {
    if (seen.has(it.key)) continue;
    seen.add(it.key);
    uniq.push(it);
    if (uniq.length >= max) break;
  }
  return uniq;
}
